import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    refreshOutline,
    statsChartOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import * as Components from "./StatsFunction";
import {
    GameDetail,
    GamePlay,
    Team,
    getGameDetail,
    getTeams
} from "../services/mexfbaApi";
import "./GameStats.css";

interface RouteParams {
    id: string;
}

interface TeamGameStats {
    id: string;
    rush: number;
    pass: number;
    tdR: number;
    tdP: number;
    rushD: number;
    passD: number;
    tdRD: number;
    tdPD: number;
    fr: number;
    int: number;
    attXp: number;
    xp: number;
    attFg: number;
    fg: number;
    ydsKR: number;
    ydsPR: number;
    tdKR: number;
    tdPR: number;
    kb: number;
    sack: number;
    conv: number;
    attR: number;
    attP: number;
    comp: number;
    penOff: number;
    penDef: number;
    ydPenOff: number;
    ydPenDef: number;
}

interface ComparisonRowProps {
    label: string;
    homeValue: string | number;
    awayValue: string | number;
    homeBarValue?: number;
    awayBarValue?: number;
}

function getNumber(value: unknown): number {
    const numberValue = Number(value);

    return Number.isFinite(numberValue) ? numberValue : 0;
}

function safeDivide(
    dividend: number,
    divisor: number,
    decimals = 1
): string {
    if (divisor === 0) {
        return "0.0";
    }

    return (dividend / divisor).toFixed(decimals);
}

function getPlays(detail: GameDetail | null): GamePlay[] {
    if (!detail?.Jugadas) {
        return [];
    }

    return Array.isArray(detail.Jugadas)
        ? detail.Jugadas
        : Object.values(detail.Jugadas);
}

function calculateTeamStats(
    plays: GamePlay[],
    teamId: string
): TeamGameStats {
    const stats: TeamGameStats[] = [];

    Components.setStatsTeamAlt(teamId, stats);

    plays.forEach(function (play) {
        if (play.ofensiva === teamId) {
            Components.getOfence(play, stats);
            Components.getPenalty(play, stats);
            Components.getSpecialK(play, stats);
        } else {
            Components.getDefencee(play, stats, teamId);
            Components.getSpecialR(play, stats, teamId);
        }
    });

    return stats[0];
}

const ComparisonRow: React.FC<ComparisonRowProps> = function ({
    label,
    homeValue,
    awayValue,
    homeBarValue,
    awayBarValue
}) {
    const homeNumber =
        homeBarValue === undefined
            ? getNumber(homeValue)
            : homeBarValue;

    const awayNumber =
        awayBarValue === undefined
            ? getNumber(awayValue)
            : awayBarValue;

    const maximum = Math.max(homeNumber, awayNumber, 1);
    const homePercentage = Math.max(
        4,
        Math.round((homeNumber / maximum) * 100)
    );

    const awayPercentage = Math.max(
        4,
        Math.round((awayNumber / maximum) * 100)
    );

    return (
        <div className="game-stats-row">
            <div className="game-stats-row__numbers">
                <strong>{homeValue}</strong>
                <span>{label}</span>
                <strong>{awayValue}</strong>
            </div>

            <div className="game-stats-row__bars">
                <div className="game-stats-row__bar game-stats-row__bar--home">
                    <span style={{ width: `${homePercentage}%` }} />
                </div>

                <div className="game-stats-row__bar game-stats-row__bar--away">
                    <span style={{ width: `${awayPercentage}%` }} />
                </div>
            </div>
        </div>
    );
};

interface StatsSectionProps {
    title: string;
    children: React.ReactNode;
}

const StatsSection: React.FC<StatsSectionProps> = function ({
    title,
    children
}) {
    return (
        <section className="game-stats-section">
            <h2>{title}</h2>
            <div className="game-stats-section__content">
                {children}
            </div>
        </section>
    );
};

const GameStats: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [gameDetail, setGameDetail] =
        useState<GameDetail | null>(null);
    const [teams, setTeams] = useState<Team[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadGame = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const [detail, teamsData] = await Promise.all([
                    getGameDetail(id, forceRefresh),
                    getTeams(forceRefresh)
                ]);

                setGameDetail(detail);
                setTeams(teamsData);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar estadísticas del partido:",
                    requestError
                );

                setError(
                    "No fue posible cargar las estadísticas del partido."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadGame();
        },
        [loadGame]
    );

    const teamsById = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [team.id, team];
            })
        );
    }, [teams]);

    const plays = useMemo(
        function () {
            return getPlays(gameDetail);
        },
        [gameDetail]
    );

    const homeStats = useMemo(
        function (): TeamGameStats | null {
            if (!gameDetail) {
                return null;
            }

            return calculateTeamStats(
                plays,
                gameDetail.Data.id_local
            );
        },
        [gameDetail, plays]
    );

    const awayStats = useMemo(
        function (): TeamGameStats | null {
            if (!gameDetail) {
                return null;
            }

            return calculateTeamStats(
                plays,
                gameDetail.Data.id_visita
            );
        },
        [gameDetail, plays]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadGame(true);
        event.detail.complete();
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Estadísticas</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent>
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (!gameDetail || !homeStats || !awayStats || error) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Estadísticas</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="game-stats-content">
                    <div className="game-stats-empty">
                        <IonIcon icon={statsChartOutline} />
                        <strong>
                            No fue posible cargar las estadísticas
                        </strong>
                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadGame(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const game = gameDetail.Data;
    const localTeam = teamsById.get(game.id_local);
    const visitorTeam = teamsById.get(game.id_visita);

    const homeTotalYards = homeStats.pass + homeStats.rush;
    const awayTotalYards = awayStats.pass + awayStats.rush;

    const homeTotalPlays = homeStats.attP + homeStats.attR;
    const awayTotalPlays = awayStats.attP + awayStats.attR;

    const homeTurnovers = homeStats.int + homeStats.fr;
    const awayTurnovers = awayStats.int + awayStats.fr;

    const homePenalties = homeStats.penOff + homeStats.penDef;
    const awayPenalties = awayStats.penOff + awayStats.penDef;

    const homePenaltyYards =
        homeStats.ydPenOff + homeStats.ydPenDef;

    const awayPenaltyYards =
        awayStats.ydPenOff + awayStats.ydPenDef;

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="game-stats-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar al partido"
                            onClick={function () {
                                history.push(`/game/${id}`);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Estadísticas</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar estadísticas"
                            disabled={refreshing}
                            onClick={function () {
                                loadGame(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="game-stats-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando estadísticas..."
                    />
                </IonRefresher>

                <section className="game-stats-score">
                    <button
                        type="button"
                        onClick={function () {
                            history.push(`/team/${game.id_local}`);
                        }}
                    >
                        {localTeam?.logo ? (
                            <img
                                src={localTeam.logo}
                                alt={`Logo de ${game.nombre_local}`}
                            />
                        ) : (
                            <span>
                                {game.nombre_local?.charAt(0) || "L"}
                            </span>
                        )}

                        <strong>{game.nombre_local || "Local"}</strong>
                    </button>

                    <div>
                        <strong>{game.resLocal ?? "0"}</strong>
                        <span>—</span>
                        <strong>{game.resVisita ?? "0"}</strong>
                    </div>

                    <button
                        type="button"
                        onClick={function () {
                            history.push(`/team/${game.id_visita}`);
                        }}
                    >
                        {visitorTeam?.logo ? (
                            <img
                                src={visitorTeam.logo}
                                alt={`Logo de ${game.nombre_visita}`}
                            />
                        ) : (
                            <span>
                                {game.nombre_visita?.charAt(0) || "V"}
                            </span>
                        )}

                        <strong>
                            {game.nombre_visita || "Visitante"}
                        </strong>
                    </button>
                </section>

                <nav className="game-stats-navigation">
                    <IonButton
                        fill="clear"
                        onClick={function () {
                            history.push(`/game/${id}`);
                        }}
                    >
                        Datos
                    </IonButton>

                    <IonButton
                        fill="clear"
                        onClick={function () {
                            history.push(`/gamePlays/${id}`);
                        }}
                    >
                        Jugadas
                    </IonButton>

                    <IonButton
                        fill="clear"
                        onClick={function () {
                            history.push(`/gamePlayers/${id}`);
                        }}
                    >
                        Jugadores
                    </IonButton>

                    <IonButton fill="solid">Stats</IonButton>
                </nav>

                <main className="game-stats-list">
                    <div className="game-stats-list__heading">
                        <div>
                            <span>COMPARATIVA DEL PARTIDO</span>
                            <h1>Estadísticas por equipo</h1>
                        </div>

                        <IonIcon icon={statsChartOutline} />
                    </div>

                    <div className="game-stats-teams">
                        <span>{game.nombre_local || "Local"}</span>
                        <span>{game.nombre_visita || "Visitante"}</span>
                    </div>

                    <StatsSection title="Ofensiva">
                        <ComparisonRow
                            label="Yardas totales"
                            homeValue={homeTotalYards}
                            awayValue={awayTotalYards}
                        />

                        <ComparisonRow
                            label="Jugadas"
                            homeValue={homeTotalPlays}
                            awayValue={awayTotalPlays}
                        />

                        <ComparisonRow
                            label="Yardas por jugada"
                            homeValue={safeDivide(
                                homeTotalYards,
                                homeTotalPlays
                            )}
                            awayValue={safeDivide(
                                awayTotalYards,
                                awayTotalPlays
                            )}
                            homeBarValue={
                                homeTotalPlays > 0
                                    ? homeTotalYards / homeTotalPlays
                                    : 0
                            }
                            awayBarValue={
                                awayTotalPlays > 0
                                    ? awayTotalYards / awayTotalPlays
                                    : 0
                            }
                        />
                    </StatsSection>

                    <StatsSection title="Pase">
                        <ComparisonRow
                            label="Completos / intentos"
                            homeValue={`${homeStats.comp}/${homeStats.attP}`}
                            awayValue={`${awayStats.comp}/${awayStats.attP}`}
                            homeBarValue={homeStats.comp}
                            awayBarValue={awayStats.comp}
                        />

                        <ComparisonRow
                            label="Yardas por pase"
                            homeValue={safeDivide(
                                homeStats.pass,
                                homeStats.comp
                            )}
                            awayValue={safeDivide(
                                awayStats.pass,
                                awayStats.comp
                            )}
                            homeBarValue={
                                homeStats.comp > 0
                                    ? homeStats.pass / homeStats.comp
                                    : 0
                            }
                            awayBarValue={
                                awayStats.comp > 0
                                    ? awayStats.pass / awayStats.comp
                                    : 0
                            }
                        />

                        <ComparisonRow
                            label="Yardas aéreas"
                            homeValue={homeStats.pass}
                            awayValue={awayStats.pass}
                        />

                        <ComparisonRow
                            label="Touchdowns por pase"
                            homeValue={homeStats.tdP}
                            awayValue={awayStats.tdP}
                        />
                    </StatsSection>

                    <StatsSection title="Carrera">
                        <ComparisonRow
                            label="Acarreos"
                            homeValue={homeStats.attR}
                            awayValue={awayStats.attR}
                        />

                        <ComparisonRow
                            label="Yardas terrestres"
                            homeValue={homeStats.rush}
                            awayValue={awayStats.rush}
                        />

                        <ComparisonRow
                            label="Yardas por acarreo"
                            homeValue={safeDivide(
                                homeStats.rush,
                                homeStats.attR
                            )}
                            awayValue={safeDivide(
                                awayStats.rush,
                                awayStats.attR
                            )}
                            homeBarValue={
                                homeStats.attR > 0
                                    ? homeStats.rush / homeStats.attR
                                    : 0
                            }
                            awayBarValue={
                                awayStats.attR > 0
                                    ? awayStats.rush / awayStats.attR
                                    : 0
                            }
                        />

                        <ComparisonRow
                            label="Touchdowns por carrera"
                            homeValue={homeStats.tdR}
                            awayValue={awayStats.tdR}
                        />
                    </StatsSection>

                    <StatsSection title="Defensiva y pérdidas">
                        <ComparisonRow
                            label="Intercepciones"
                            homeValue={homeStats.int}
                            awayValue={awayStats.int}
                        />

                        <ComparisonRow
                            label="Fumbles recuperados"
                            homeValue={homeStats.fr}
                            awayValue={awayStats.fr}
                        />

                        <ComparisonRow
                            label="Sacks"
                            homeValue={homeStats.sack}
                            awayValue={awayStats.sack}
                        />

                        <ComparisonRow
                            label="Balones recuperados"
                            homeValue={homeTurnovers}
                            awayValue={awayTurnovers}
                        />
                    </StatsSection>

                    <StatsSection title="Penalizaciones">
                        <ComparisonRow
                            label="Penalizaciones"
                            homeValue={homePenalties}
                            awayValue={awayPenalties}
                        />

                        <ComparisonRow
                            label="Yardas de castigo"
                            homeValue={homePenaltyYards}
                            awayValue={awayPenaltyYards}
                        />
                    </StatsSection>

                    <StatsSection title="Equipos especiales">
                        <ComparisonRow
                            label="Puntos extra"
                            homeValue={`${homeStats.xp}/${homeStats.attXp}`}
                            awayValue={`${awayStats.xp}/${awayStats.attXp}`}
                            homeBarValue={homeStats.xp}
                            awayBarValue={awayStats.xp}
                        />

                        <ComparisonRow
                            label="Goles de campo"
                            homeValue={`${homeStats.fg}/${homeStats.attFg}`}
                            awayValue={`${awayStats.fg}/${awayStats.attFg}`}
                            homeBarValue={homeStats.fg}
                            awayBarValue={awayStats.fg}
                        />

                        <ComparisonRow
                            label="Yardas en regresos"
                            homeValue={
                                homeStats.ydsKR + homeStats.ydsPR
                            }
                            awayValue={
                                awayStats.ydsKR + awayStats.ydsPR
                            }
                        />

                        <ComparisonRow
                            label="Patadas bloqueadas"
                            homeValue={homeStats.kb}
                            awayValue={awayStats.kb}
                        />
                    </StatsSection>
                </main>
            </IonContent>
        </IonPage>
    );
};

export default GameStats;
